/**
 * deviceMutations.js -- Immutable write operations on sysconfig device data.
 * Every function returns a new sysconfig (clone); the original is never mutated.
 *
 * ID-guarantee and add/legacy-update functions are async because UUID
 * generation calls the backend API via generateDeviceId().
 *
 * @typedef {import("../types/deviceTypes").DeviceFormValues} DeviceFormValues
 */

import {
  findDeviceById,
  getAllMeterDeviceCollections,
  couplableDevicesOnMeter,
  DEVICE_COLLECTIONS,
} from "./deviceSelectors";
import { resolveDeviceTarget } from "./deviceFactory";
import { generateDeviceId } from "../api/uuidService";
import { validateFormData } from "./deviceValidation";
import {
  COUPLING_LINKS,
  ALL_COUPLING_KEYS,
  isCouplableType,
  couplingKeyFor,
} from "./deviceCoupling";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function cloneSysconfig(sysconfig) {
  if (typeof structuredClone === "function") {
    return structuredClone(sysconfig);
  }
  return JSON.parse(JSON.stringify(sysconfig));
}

function findDeviceByIdentifier(sysconfig, identifier) {
  const collections = getAllMeterDeviceCollections(sysconfig);
  for (const c of collections) {
    const index = c.devices.findIndex((dev) => {
      if (dev.device_id && dev.device_id === identifier) return true;
      if (dev.serial_number && dev.serial_number === identifier) return true;
      if (dev.name && dev.name === identifier) return true;
      if (dev.device_name && dev.device_name === identifier) return true;
      return false;
    });
    if (index !== -1) {
      return {
        meterIndex: c.meterIndex,
        meter: c.meter,
        collectionName: c.collectionName,
        type: c.type,
        deviceIndex: index,
        device: c.devices[index],
      };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Coupling (in-place helpers; the exported mutations clone first)
// ---------------------------------------------------------------------------

/**
 * Strip `deviceId` from every coupling list on every device of a meter.
 * @param {Object} meter
 * @param {string} deviceId
 */
function stripIdFromMeterCouplings(meter, deviceId) {
  couplableDevicesOnMeter(meter).forEach(({ raw }) => {
    ALL_COUPLING_KEYS.forEach((key) => {
      if (Array.isArray(raw[key]) && raw[key].includes(deviceId)) {
        raw[key] = raw[key].filter((id) => id !== deviceId);
      }
    });
  });
}

/**
 * Write a device's couplings on both sides, in place.
 *
 * The device's own coupling keys are rewritten from `partnerIds` (grouped
 * by partner kind under the key its type uses for that kind); the device is
 * removed from every other device's lists on the meter and then added back
 * to each chosen partner's list under the partner's key for this device's
 * kind. Ids that do not name a couplable device on the same meter, or that
 * name an unsupported pair, are dropped rather than written: the engine
 * refuses a sysconfig whose coupling names a device that is not there.
 *
 * @param {Object} meter - the meter object inside an already-cloned sysconfig
 * @param {Object} device - the device object inside that meter
 * @param {string} type - the device's couplable type
 * @param {string[]} partnerIds
 */
function applyCouplingsInPlace(meter, device, type, partnerIds) {
  const devices = couplableDevicesOnMeter(meter);
  const deviceId = device.device_id;

  const chosen = [];
  const seen = new Set();
  (partnerIds || []).forEach((id) => {
    if (id === deviceId || seen.has(id)) return;
    const partner = devices.get(id);
    if (!partner || !couplingKeyFor(type, partner.type)) return;
    seen.add(id);
    chosen.push(partner);
  });

  // This side: one list per key, in the order the partners were chosen.
  COUPLING_LINKS[type].forEach(({ key, partners }) => {
    device[key] = chosen
      .filter((p) => partners.includes(p.type))
      .map((p) => p.device_id);
  });

  // The other side: clear every stale mention, then write the chosen ones.
  stripIdFromMeterCouplings(meter, deviceId);
  chosen.forEach((partner) => {
    const key = couplingKeyFor(partner.type, type);
    const list = Array.isArray(partner.raw[key]) ? partner.raw[key] : [];
    if (!list.includes(deviceId)) {
      partner.raw[key] = [...list, deviceId];
    }
  });
}

/**
 * Copy whatever coupling keys `previous` carried onto `next`, so a rebuild
 * from form values that said nothing about couplings keeps the links the
 * device already had instead of resetting them to []. This is what makes an
 * edit-and-save round trip preserve a coupling written by hand or by an
 * earlier save.
 * @param {Object} previous
 * @param {Object} next
 */
function carryCouplingKeys(previous, next) {
  ALL_COUPLING_KEYS.forEach((key) => {
    if (Array.isArray(previous?.[key])) {
      next[key] = [...previous[key]];
    }
  });
}

/**
 * Immutable: set the devices `deviceId` is coupled to, writing both sides.
 * @param {Object} sysconfig
 * @param {string} deviceId
 * @param {string[]} partnerIds - the complete new set of partners
 * @returns {Object} new sysconfig; the original, unchanged, if the device is
 *   not found or is not a couplable kind
 */
export function setDeviceCouplings(sysconfig, deviceId, partnerIds) {
  const cloned = cloneSysconfig(sysconfig);
  const loc = findDeviceById(cloned, deviceId);
  if (!loc || !isCouplableType(loc.type)) {
    return sysconfig;
  }
  applyCouplingsInPlace(loc.meter, loc.device, loc.type, partnerIds);
  return cloned;
}

/**
 * Immutable: remove `deviceId` from every other device's coupling lists,
 * on every meter. Its own lists are left as they are; this is the cascade a
 * delete runs before dropping the device.
 * @param {Object} sysconfig
 * @param {string} deviceId
 * @returns {Object} new sysconfig
 */
export function removeDeviceFromCouplings(sysconfig, deviceId) {
  const cloned = cloneSysconfig(sysconfig);
  if (!deviceId) return cloned;
  (cloned?.Microgrid?.meters || []).forEach((meter) => {
    stripIdFromMeterCouplings(meter, deviceId);
  });
  return cloned;
}

// ---------------------------------------------------------------------------
// ID guarantees
// ---------------------------------------------------------------------------

/**
 * Returns a new sysconfig where every meter has a stable `id`.
 * Idempotent: meters that already have an `id` are left unchanged.
 * @param {Object} sysconfig
 * @returns {Promise<Object>} new sysconfig (clone)
 */
export async function ensureMeterIds(sysconfig) {
  const cloned = cloneSysconfig(sysconfig);
  const meters = cloned?.Microgrid?.meters || [];

  const pending = meters.filter((m) => !m.id);
  const ids = await Promise.all(pending.map(() => generateDeviceId()));
  pending.forEach((meter, i) => {
    meter.id = ids[i];
  });

  return cloned;
}

/**
 * Returns a new sysconfig where every device has a `device_id`.
 * Idempotent: devices that already have a `device_id` are left unchanged.
 * @param {Object} sysconfig
 * @returns {Promise<Object>} new sysconfig (clone)
 */
export async function ensureDeviceIds(sysconfig) {
  const cloned = cloneSysconfig(sysconfig);
  const collections = getAllMeterDeviceCollections(cloned);

  const pending = [];
  collections.forEach(({ devices }) => {
    devices.forEach((dev) => {
      if (!dev.device_id) {
        pending.push(dev);
      }
    });
  });

  const ids = await Promise.all(pending.map(() => generateDeviceId()));
  pending.forEach((dev, i) => {
    dev.device_id = ids[i];
  });

  return cloned;
}

// ---------------------------------------------------------------------------
// Primary mutations (device_id based)
// ---------------------------------------------------------------------------

/**
 * Immutable add: returns a new sysconfig with a new device appended
 * to the target meter's appropriate collection.
 *
 * @param {Object} sysconfig
 * @param {DeviceFormValues} formValues
 * @param {{ meterId?: string }} [options]
 * @returns {Promise<Object>} new sysconfig
 */
export async function addDeviceToSysconfig(sysconfig, formValues, { meterId } = {}) {
  const cloned = cloneSysconfig(sysconfig);
  let meters = cloned?.Microgrid?.meters;

  if (!Array.isArray(meters) || meters.length === 0) {
    meters = [
      {
        id: await generateDeviceId(),
        name: "Meter",
        make_and_model: "Unknown Model",
        serial_number: "",
        utility_tariff_csv: "",
        building_load: { historical_load_csv: "" },
        evs: [],
        batteries: [],
        solar_arrays: [],
        fueled_generators: [],
        uncontrollable_loads: [],
        controllable_loads: [],
      },
    ];
    cloned.Microgrid = cloned.Microgrid || {};
    cloned.Microgrid.meters = meters;
  }

  let meter;
  if (meterId) {
    meter = meters.find((m) => m.id === meterId);
    if (!meter) {
      throw new Error(`Meter with id=${meterId} not found`);
    }
  } else {
    meter = meters[0];
  }

  const target = resolveDeviceTarget(formValues);

  if (target.isMeterLevel) {
    Object.assign(meter, target.buildConfig(formValues));
  } else {
    meter[target.collectionName] = meter[target.collectionName] || [];
    const deviceId = await generateDeviceId();
    const config = target.buildConfig(formValues, { deviceId });
    meter[target.collectionName].push(config);

    // A new device coupled to existing ones: the existing devices' lists
    // are updated in this same save, so the JSON never carries one side.
    if (Array.isArray(formValues.coupled_device_ids)) {
      const type = collectionType(target.collectionName);
      if (isCouplableType(type)) {
        applyCouplingsInPlace(meter, config, type, formValues.coupled_device_ids);
      }
    }
  }

  return cloned;
}

/**
 * The couplable device type a collection name holds, for the mutations that
 * only know the collection they are writing into.
 * @param {string} collectionName
 * @returns {string|null}
 */
function collectionType(collectionName) {
  const found = DEVICE_COLLECTIONS.find((c) => c.collectionName === collectionName);
  return found ? found.type : null;
}

/**
 * Immutable update by device_id. Preserves the existing device_id.
 * @param {Object} sysconfig
 * @param {string} device_id
 * @param {DeviceFormValues} formValues
 * @returns {Promise<Object>} new sysconfig
 * @throws {Error} if validation fails, device not found, or target unresolvable
 */
export async function updateDeviceById(sysconfig, device_id, formValues) {
  validateFormData(formValues);

  const cloned = cloneSysconfig(sysconfig);
  const loc = findDeviceById(cloned, device_id);
  if (!loc) {
    throw new Error(`Device with device_id=${device_id} not found`);
  }
  const { meter, collectionName, deviceIndex, device: previous, type } = loc;
  const target = resolveDeviceTarget(formValues);
  if (!target) {
    throw new Error(`Device target not found for device_id=${device_id}`);
  }
  const config = target.buildConfig(formValues, { deviceId: device_id });

  // The factory builds from form values alone and knows nothing about the
  // couplings the device already has. Carry them over before the swap, so a
  // save that did not touch the picker changes no link; then, if the form
  // did say who this device is coupled to, write that on both sides.
  carryCouplingKeys(previous, config);
  meter[collectionName][deviceIndex] = config;

  if (Array.isArray(formValues.coupled_device_ids) && isCouplableType(type)) {
    applyCouplingsInPlace(meter, config, type, formValues.coupled_device_ids);
  }
  return cloned;
}

/**
 * Immutable delete by device_id. Idempotent (no error if not found).
 * @param {Object} sysconfig
 * @param {string} device_id
 * @returns {Object} new sysconfig
 */
export function deleteDeviceById(sysconfig, device_id) {
  if (!device_id) {
    return sysconfig;
  }
  const cloned = cloneSysconfig(sysconfig);
  const loc = findDeviceById(cloned, device_id);
  if (!loc) {
    return cloned;
  }

  const { meter, collectionName } = loc;
  // Cascade: no other device may keep pointing at an id that is about to
  // stop existing - the engine refuses a coupling to a missing device.
  stripIdFromMeterCouplings(meter, device_id);
  meter[collectionName] = meter[collectionName].filter(
    (d) => d.device_id !== device_id,
  );
  return cloned;
}

// ---------------------------------------------------------------------------
// Deprecated — kept for backward compat with legacy components
// ---------------------------------------------------------------------------

/**
 * @deprecated Use deleteDeviceById after ensuring device_id via ensureDeviceIds.
 */
export function removeDeviceByIdentifier(sysconfig, identifier) {
  const cloned = cloneSysconfig(sysconfig);
  const loc = findDeviceByIdentifier(cloned, identifier);
  if (!loc) {
    return cloned;
  }

  const { meter, collectionName, deviceIndex } = loc;
  meter[collectionName].splice(deviceIndex, 1);
  return cloned;
}

/**
 * @deprecated Use updateDeviceById after ensuring device_id via ensureDeviceIds.
 */
export async function updateDeviceByName(sysconfig, deviceName, formValues) {
  const cloned = cloneSysconfig(sysconfig);
  const nameLower = (deviceName || "").toLowerCase();
  const target = resolveDeviceTarget(formValues);
  if (!target) {
    console.error(`Device target not found for device name=${deviceName}`);
    return sysconfig;
  }

  if (target.isMeterLevel) {
    const meters = cloned?.Microgrid?.meters || [];
    for (const meter of meters) {
      if ((meter.name || "").toLowerCase() === nameLower) {
        Object.assign(meter, target.buildConfig(formValues));
        return cloned;
      }
    }
    console.error(`Meter with name=${deviceName} not found`);
    return sysconfig;
  }

  const collections = getAllMeterDeviceCollections(cloned);
  for (const c of collections) {
    if (c.collectionName !== target.collectionName) continue;
    const idx = c.devices.findIndex(
      (d) => (d.name || "").toLowerCase() === nameLower,
    );
    if (idx !== -1) {
      const deviceId = c.devices[idx].device_id || await generateDeviceId();
      const config = target.buildConfig(formValues, { deviceId });
      carryCouplingKeys(c.devices[idx], config);
      c.meter[c.collectionName][idx] = config;
      return cloned;
    }
  }
  throw new Error(`Device with name=${deviceName} not found`);
}
